import React, { useEffect, useState } from 'react';
import { Button, Flex, Text, Spinner, View } from '@smarterservices/smarter-ui';
import { getStore, useCheckinStore, useExamSessionStore } from '../../../stores/StoreContext';
import ButtonWithConfirmation from '@ui/shared/components/Common/ButtonWithConfirmation';
import DenyConfirmation from './DenyConfirmation';
import SendToOnboardingStepDropdown from './SendToOnboardingStepDropdown';
import SectionToolbar from '../SectionToolbar';
import InfoIcon from '@ui/shared/components/Common/InfoIcon';

/**
 * ApproveDenyControls Component
 * 
 * Renders the approve/deny action buttons for session check-in, along with
 * the send to onboarding step dropdown and deny confirmation modal. Manages
 * different UI states based on the user's current journey step and send-back status.
 * 
 * @component
 * 
 * @description
 * This component displays different UI states based on the check-in process:
 * 
 * **Sending Back State** (`sendingBack === true`):
 * - Shows spinner with "Sending test taker back..." message
 * - Displays Deny button (enabled)
 * - Triggered when proctor sends user back to onboarding steps
 * - Automatically cleared when journey updates
 * 
 * **Cannot Control State** (`canControl === false`):
 * - Shows current step name with info icon
 * - Displays Deny button (enabled)
 * - Occurs when user is not on 'ConnectedToProctor' step
 * - Prevents approval until user completes required steps
 * 
 * **Normal State** (`canControl === true`):
 * - Shows "Send to Step" dropdown
 * - Shows Approve button with confirmation
 * - Shows Deny button (disabled when !canControl)
 * - Full control available when user is on 'ConnectedToProctor' step
 * 
 * **Approving State** (`isApproving === true`):
 * - Shows spinner with "Approving session..." message
 * - No action buttons available during approval process
 * 
 * @returns {JSX.Element} Toolbar with approve/deny controls and confirmation modal
 * 
 * @example
 * // Used within Checkin component
 * <ApproveDenyControls />
 */
function ApproveDenyControls() {
    const CheckinStore = getStore('checkinStore');
    const ExamSessionStore = getStore('examSessionStore');
    const journey = useExamSessionStore((state) => state.journey);
    const isApproving = useCheckinStore((state) => state.isApproving);
    const [canControl, setCanControl] = useState(false);
    const [currentStep, setCurrentStep] = useState(null);
    const [sendingBack, setSendingBack] = useState(false);


    useEffect(() => {
        const currentStep = ExamSessionStore.getCurrentJourneyStep();
        setCurrentStep(currentStep);
        // whenenver the journey has updated, that means they have been sent back so lets clear the sending back state...
        setSendingBack(false);

        if (currentStep.stepId === 'ConnectedToProctor') {
            setCanControl(true);
        } else {
            setCanControl(false);
        }
    }, [journey]);

    const renderDenyButton = (disabled = false) => {
        return (
            <Button
                color="danger"
                size="small"
                disabled={disabled}
                onClick={() => CheckinStore.setDenyConfirmationOpen(true)}
            >
                Deny
            </Button>
        );
    };

    if (sendingBack) {
        return (
            <>
                <SectionToolbar>
                    <Flex.Item>
                        <Spinner size="x-small" margin="0 small 0 0" />
                        <Text variant='contentSmall'>Sending test taker back...</Text>
                    </Flex.Item>
                    <Flex.Item>
                        {renderDenyButton()}
                    </Flex.Item>
                </SectionToolbar>
                <DenyConfirmation />
            </>
        )
    }

    if (!canControl) {
        return (
            <>
                <SectionToolbar>
                    <Flex.Item>
                        <View as="span" margin="0 x-small 0 0">
                            <Text variant='contentSmall'>
                                Current step: <strong>{currentStep?.name}</strong>
                            </Text>
                        </View>
                        <InfoIcon
                            tip="Since the user was sent back to a step, you will need to wait to approve the session until all steps are completed."
                            placement="top"
                        />
                    </Flex.Item>
                    <Flex.Item>
                        {renderDenyButton()}
                    </Flex.Item>
                </SectionToolbar>
                <DenyConfirmation />
            </>
        )
    }

    return (
        <>
            <SectionToolbar>
                {!isApproving ? (
                    <>
                        <Flex.Item>
                            <SendToOnboardingStepDropdown onSendbackStart={() => setSendingBack(true)} />
                        </Flex.Item>
                        <Flex.Item>
                            <ButtonWithConfirmation
                                variant="button"
                                onConfirm={CheckinStore.approveSession}
                                screenReaderLabel="Approve Session"
                                confirmationText="Are you sure you want to approve this session?"
                                confirmButtonText="Yes"
                                confirmButtonColor="success"
                                cancelButtonText="No"
                                color="success"
                                disabled={!canControl}
                            >
                                Approve
                            </ButtonWithConfirmation>

                            {renderDenyButton(!canControl)}
                        </Flex.Item>
                    </>
                ) : (
                    <Flex.Item textAlign='center' alignItems="end" shouldGrow>
                        <Spinner size="x-small" margin="0 small 0 0" />
                        <Text variant='contentSmall'>Approving session...</Text>
                    </Flex.Item>
                )}
            </SectionToolbar>

            <DenyConfirmation />
        </>
    );
}

export default ApproveDenyControls;
